import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  Billboard,
  Environment,
  Html,
  OrbitControls,
  Sparkles,
  Stars,
  useGLTF,
} from '@react-three/drei';

// Coloca los logos en public/apps con estos nombres de archivo.
const ORBIT_APPS = [
  { name: 'Netflix', short: 'N', icon: '/apps/netflix.png', zoom: 1.16 },
  { name: 'Prime Video', short: 'P', icon: '/apps/prime.png', zoom: 1.36 },
  { name: 'Disney+', short: 'D', icon: '/apps/disney.png', zoom: 1.44 },
  { name: 'Max', short: 'M', icon: '/apps/max.png', zoom: 1.34 },
  { name: 'Crunchyroll', short: 'C', icon: '/apps/crunchyroll.png', zoom: 1.28 },
  { name: 'Apple TV', short: 'A', icon: '/apps/apple.png', zoom: 1.22 },
  { name: 'Hulu', short: 'H', icon: '/apps/hulu.png', zoom: 1.3 },
  { name: 'Paramount+', short: 'PA', icon: '/apps/paramount.png', zoom: 1.3 },
  { name: 'Pluto TV', short: 'PL', icon: '/apps/pluto.png', zoom: 1.26 },
  { name: 'Star+', short: 'S', icon: '/apps/star.png', zoom: 1.38 },
  { name: 'Twitch', short: 'TW', icon: '/apps/twitch.png', zoom: 1.24 },
  { name: 'Vix', short: 'VX', icon: '/apps/vix.png', zoom: 1.3 },
  { name: 'Roku', short: 'R', icon: '/apps/roku.jpg', zoom: 1.2 },
];

const BUDA_BASE_Y = 1.7;
const BUDA_TARGET_SIZE = 4.0;
const BUDA_MODEL_PATH = '/buda_sin_fondo.glb';
const BUDA_MODEL_FIX_ROTATION = [0, 0, 0];
const BUDA_VERTICAL_OFFSET = -1.9;
const ORBIT_CENTER = [0, BUDA_BASE_Y + BUDA_VERTICAL_OFFSET - 1.22, 0.18];
const ORBIT_RADIUS = 1.24;
const ORBIT_SPEED = 0.52;

const badgeStyle = {
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(13, 13, 13, 0.22)',
  border: '1px solid rgba(255, 122, 26, 0.2)',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
  color: '#fff3e8',
  fontFamily: 'Outfit, sans-serif',
  fontWeight: 700,
  fontSize: 13,
  overflow: 'hidden',
};

const GhostPlayIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
    <path d="M8 6.5V17.5L17 12L8 6.5Z" />
  </svg>
);

const GhostSignalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M3.5 9.5A12 12 0 0 1 20.5 9.5" />
    <path d="M6.5 12.5A8 8 0 0 1 17.5 12.5" />
    <path d="M9.5 15.5A4 4 0 0 1 14.5 15.5" />
    <circle cx="12" cy="18.3" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const GhostScreenIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M8.5 19.2H15.5M12 17V19.2" />
  </svg>
);

const GhostCatalogIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="4" y="4.5" width="16" height="15" rx="2.2" />
    <path d="M8 9.5H16M8 13.5H14" />
    <circle cx="8" cy="17" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

const GhostRocketIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M10 13.8L13.6 10.2C16.4 7.4 17.2 4.8 17.2 4.8S14.6 5.6 11.8 8.4L8.2 12" />
    <path d="M7.5 13L4.8 15.7M11 16.5L8.3 19.2" />
    <path d="M9.5 9.8L14.2 14.5" />
  </svg>
);

const DownArrowIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9L12 15L18 9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StreamingMarks = () => {
  return (
    <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
      <div className="absolute -right-20 -top-16 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
      <div className="absolute -left-20 top-1/3 h-60 w-60 rounded-full bg-brand-soft/10 blur-3xl" />
      <div className="absolute bottom-8 left-1/2 h-32 w-[80%] -translate-x-1/2 rounded-full border border-brand/20 bg-brand/5 blur-2xl" />

      <span className="absolute left-[11%] top-[22%] inline-flex h-9 w-9 items-center justify-center rounded-xl border border-brand/25 bg-black/30 text-brand/40">
        <GhostPlayIcon />
      </span>
      <span className="absolute right-[13%] top-[26%] inline-flex h-9 w-9 items-center justify-center rounded-xl border border-brand/25 bg-black/30 text-brand/40">
        <GhostSignalIcon />
      </span>
      <span className="absolute right-[20%] bottom-[20%] inline-flex h-9 w-9 items-center justify-center rounded-xl border border-brand/25 bg-black/30 text-brand/40">
        <GhostScreenIcon />
      </span>
      <span className="absolute left-[21%] top-[14%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostCatalogIcon />
      </span>
      <span className="absolute right-[24%] top-[16%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostPlayIcon />
      </span>
      <span className="absolute left-[7%] top-[45%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostSignalIcon />
      </span>
      <span className="absolute right-[8%] top-[54%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostCatalogIcon />
      </span>
      <span className="absolute left-[15%] bottom-[25%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostRocketIcon />
      </span>
      <span className="absolute right-[30%] bottom-[30%] inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/35">
        <GhostScreenIcon />
      </span>
      <span className="absolute left-[33%] top-[34%] inline-flex h-7 w-7 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/30">
        <GhostPlayIcon />
      </span>
      <span className="absolute right-[37%] top-[39%] inline-flex h-7 w-7 items-center justify-center rounded-lg border border-brand/20 bg-black/25 text-brand/30">
        <GhostSignalIcon />
      </span>
    </div>
  );
};

const AppBadge = ({ app }) => {
  const [hasError, setHasError] = useState(false);

  return (
    <div style={badgeStyle}>
      {hasError ? (
        <span>{app.short}</span>
      ) : (
        <img
          src={app.icon}
          alt={app.name}
          onError={() => setHasError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            transform: `scale(${app.zoom ?? 1.14}) translateZ(0)`,
            imageRendering: 'auto',
            backfaceVisibility: 'hidden',
            opacity: 0.85,
            filter: 'contrast(1.08) saturate(1.06)',
          }}
        />
      )}
    </div>
  );
};

const OrbitingApps = () => {
  const iconRefs = useRef([]);

  useFrame(({ clock }) => {
    const elapsed = clock.getElapsedTime();
    iconRefs.current.forEach((item, index) => {
      if (!item) return;
      const phase = index * ((Math.PI * 2) / ORBIT_APPS.length);
      const angle = elapsed * ORBIT_SPEED + phase;
      item.position.set(
        Math.cos(angle) * ORBIT_RADIUS,
        Math.sin(angle * 2) * 0.018,
        Math.sin(angle) * ORBIT_RADIUS
      );
    });
  });

  return (
    <group position={ORBIT_CENTER}>
      {ORBIT_APPS.map((app, index) => (
        <group key={app.name} ref={(node) => { iconRefs.current[index] = node; }}>
          <Billboard follow lockX={false} lockY={false} lockZ={false}>
            <Html center position={[0, 0, 0]} zIndexRange={[6, 0]}>
              <AppBadge app={app} />
            </Html>
          </Billboard>
        </group>
      ))}
    </group>
  );
};

const BudaModel = () => {
  const rootRef = useRef();
  const { scene } = useGLTF(BUDA_MODEL_PATH);

  const normalizedModel = useMemo(() => {
    const clone = scene.clone(true);
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const maxSize = Math.max(size.x, size.y, size.z) || 1;
    const fitScale = BUDA_TARGET_SIZE / maxSize;

    clone.position.sub(center);
    clone.scale.setScalar(fitScale);

    clone.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    return clone;
  }, [scene]);

  useFrame(({ clock }) => {
    if (rootRef.current) {
      const elapsed = clock.getElapsedTime();
      rootRef.current.rotation.y = Math.PI + Math.sin(elapsed * 0.72) * 0.22;
      rootRef.current.rotation.x = Math.sin(elapsed * 0.36) * 0.03;
      rootRef.current.position.y = BUDA_BASE_Y + BUDA_VERTICAL_OFFSET + Math.sin(elapsed * 1.3) * 0.04;
    }
  });

  return (
    <group ref={rootRef} rotation={[0, Math.PI, 0]}>
      <group rotation={BUDA_MODEL_FIX_ROTATION}>
        <primitive object={normalizedModel} />
      </group>
    </group>
  );
};

const BudaFallback = () => (
  <mesh position={[0, -0.2, 0]} castShadow receiveShadow>
    <cylinderGeometry args={[0.8, 1, 2.5, 24]} />
    <meshStandardMaterial color="#6b4a2f" />
  </mesh>
);

const Hero3D = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)');
    const apply = () => setIsTouchDevice(mediaQuery.matches);
    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', apply);
      return () => mediaQuery.removeEventListener('change', apply);
    }

    mediaQuery.addListener(apply);
    return () => mediaQuery.removeListener(apply);
  }, []);

  return (
    <section className="relative h-[640px] w-full overflow-hidden rounded-b-[2.5rem] border-b border-brand/25 bg-gradient-to-b from-surface-800 via-surface-900 to-black shadow-[0_30px_80px_rgba(0,0,0,0.55)] [touch-action:pan-y]">
      <StreamingMarks />

      <div className="pointer-events-none absolute inset-0 z-10">
        <div className="px-6 pt-10 md:px-10 md:pt-12">
          <span className="inline-flex items-center rounded-full border border-brand/60 bg-black/40 px-4 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-text-300">
            Streaming Premium
          </span>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-text-100 md:text-6xl">
            GORDITO TV STREAMING
          </h1>
          <p className="mt-4 max-w-lg text-base text-text-300 md:text-lg">
            Television por internet en hasta 3 dispositivos con canales, peliculas, series y eventos en vivo.
          </p>
        </div>
      </div>

      <Canvas
        shadows
        camera={{ position: [0.15, 1.45, 4.35], fov: 40 }}
        onCreated={({ gl }) => {
          gl.domElement.style.touchAction = 'pan-y';
        }}
      >
        <color attach="background" args={['#050505']} />
        <fog attach="fog" args={['#050505', 7, 15]} />
        <ambientLight intensity={0.6} />
        <directionalLight
          castShadow
          position={[4.4, 6.5, 3.8]}
          intensity={1.5}
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <pointLight position={[-3, 3, -2]} intensity={0.72} color="#ff7a1a" />
        <pointLight position={[0, 4.5, 1]} intensity={0.5} color="#fff6dd" />
        <Stars radius={120} depth={86} count={2600} factor={2.8} saturation={0} fade speed={0.38} />
        <Sparkles
          count={220}
          scale={[20, 10, 16]}
          size={3.5}
          speed={0.95}
          noise={1.25}
          color="#fff6de"
          opacity={0.95}
        />
        <Sparkles
          count={130}
          scale={[16, 8, 12]}
          size={4.8}
          speed={0.6}
          noise={0.85}
          color="#ffd39a"
          opacity={0.86}
        />

        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.25, 0]}>
          <ringGeometry args={[1.35, 2.05, 64]} />
          <meshBasicMaterial color="#ff7a1a" transparent opacity={0.24} />
        </mesh>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.25, 0]}>
          <ringGeometry args={[2.3, 2.65, 64]} />
          <meshBasicMaterial color="#ffb67d" transparent opacity={0.12} />
        </mesh>

        <Suspense fallback={<BudaFallback />}>
          <BudaModel />
        </Suspense>
        <OrbitingApps />
        <Environment preset="night" />

        {!isTouchDevice && (
          <OrbitControls
            enablePan={false}
            enableZoom={false}
            minPolarAngle={1.0}
            maxPolarAngle={1.34}
            target={[0, 0.5, 0]}
          />
        )}
      </Canvas>

      <a
        href="#features"
        className="absolute bottom-4 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-brand/35 bg-black/45 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-200 backdrop-blur-sm transition duration-200 hover:-translate-y-0.5 hover:border-brand/55 hover:text-text-100 md:hidden"
      >
        <span>Bajar</span>
        <span className="text-brand animate-bounce">
          <DownArrowIcon />
        </span>
      </a>
    </section>
  );
};

useGLTF.preload(BUDA_MODEL_PATH);

export default Hero3D;
